import z from 'zod'
import { nullSafeBoolean, nullSafeNumber } from '../shared/schema'

// All Soundcheck calibration lives on one document, settings/soundcheck, so a
// live operator can turn the notifications on and tune every confirmation
// period in one place without a deploy. Field names are unprefixed because the
// document already namespaces them.
//
// Health checks and telemetry run whether or not notifications are enabled; the
// only thing the switch changes is whether the dialog and banner appear. There
// is no blocking switch: AV validation and speech-driven participation depend on
// the same measurement, so turning it off never stops MicCheck.
export const schema = z.object({
  // Show the dialog and banner. Health checks and telemetry run when false.
  notificationsEnabled: nullSafeBoolean(false),
  // Account for elapsed time and evaluate pending checks on one controller clock.
  tickMS: nullSafeNumber(5000),
  // Allow microphone startup or restart to settle before confirming an unusable
  // track.
  micGraceMS: nullSafeNumber(5000),
  // Allow the meeting microphone to publish after the student joins before
  // confirming no_track. 15s: the publish lands 5-11s after av_check_passed,
  // so the 5s micGraceMS raised a false no_track for 42 of the 58 users who
  // logged one on 2026-09-24. Matches the legacy meeting_audio_track_failed
  // grace.
  publishGraceMS: nullSafeNumber(15000),
  // Separate the two digital-zero reads that confirm digital_silence.
  silenceConfirmDelayMS: nullSafeNumber(5000),
  // Re-read an active track so a mic that dies mid-session is noticed.
  silencePollIntervalMS: nullSafeNumber(15000),
  // Loudness (RMS, 0-1 full scale) a live mic must reach at least once per
  // nearSilenceMS. 0.001 (-60 dBFS): in a 10-minute sample of the worker's
  // per-minute peaks on 2026-09-24, 455 of 464 publishing mics reached 0.02
  // and the quietest working mics idled around 0.002, while mics nobody could
  // hear sat at 0.0001.
  nearSilenceRMS: nullSafeNumber(0.001),
  // How long a meeting mic may stay under nearSilenceRMS before near_silence.
  // Only a mic that has not yet been loud this page session is checked.
  nearSilenceMS: nullSafeNumber(60000),
  // The same, in the AV check, where a student waits seconds, not minutes.
  avCheckNearSilenceMS: nullSafeNumber(15000),
  // How long without any sign of transcription before a student is warned:
  // no own caption and no Secretary heartbeat listing their mic. 2 minutes: on
  // 2026-10-01 the median stretch with a mic missing from the heartbeat lasted
  // 10s, and 48 of 1,334 lasted 2 minutes or more.
  transcriptWindowMS: nullSafeNumber(120000),
  // How many times the worker may start speech on the student's mic with no
  // own caption before they are warned. One caption resets the count, so short
  // utterances that transcribe to nothing do not add up. 20: between 15:00 and
  // 16:00 UTC on 2026-10-01 it would have warned 4 of 516 students who were
  // getting captions, and a typical talker reaches it in about 8 minutes.
  maxSpeechStartsWithoutCaption: nullSafeNumber(20),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsSoundcheck = z.infer<typeof schema>
